import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Col } from 'react-flexbox-grid';
import { Paper, Subheader, RaisedButton, TextField, RadioButton, RadioButtonGroup, Checkbox, SelectField, MenuItem, DatePicker } from 'material-ui';
import { get } from 'lodash';
import { fromNow } from 'utils/dateTools';
import { connect } from 'react-redux';
import { addNotification } from 'reapop';
import Loading from '../../components/Loading';
import PanelMessage from '../../components/panels/PanelMessage';
import getAppraisalTitle from '../utils/getAppraisalTitle';
import { getPMEmployeeAppraisal, updatePMEmployeeAppraisal } from './api';
import { STATUS, ANSWER_TYPES } from './appraisalEnums';

const { FINISHED } = STATUS;
const { EMPLOYEE, MANAGER, FINAL } = ANSWER_TYPES;

const styles = {
  subheader: {
    paddingLeft: 0,
    fontSize: 18,
    fontWeight: 500,
    fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif',
  },
  root: {
    padding: 16,
  },
  header: {
    fontSize: 20,
    fontWeight: 500,
  },
  appraisalDescription: {
    marginBottom: 24,
    display: 'block',
  },
  info: {
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: 6,
    minWidth: 260,
    overflow: 'hidden',
  },
  infoMessage: {
    flex: 1,
    flexBasis: 24,
  },
  label: {
    flex: 1,
    flexBasis: 24,
    color: 'rgb(176, 176, 176)',
  },
  errorText: {
    color: 'rgb(244, 67, 54)',
  },
  questionRoot: {
    padding: 16,
  },
  question: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 16,
    fontWeight: 500,
  },
  description: {
    color: 'rgb(95, 95, 95)',
    fontSize: 14,
    fontWeight: 'normal',
  },
  input: {
    marginBottom: 16,
    marginTop: 8,
    // padding: 8,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  action: {
    marginLeft: 16,
  },
  groupTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: 'rgb(0, 0, 0, 0.87)',
    marginBottom: 8,
    marginTop: 8,
  },
};

const PMAppraisal = ({ params, dispatch, valuesByKey }, context) => {
  const { appraisalId } = params;
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [language, setLanguage] = useState('IS');
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchAppraisal = async () => {
      try {
        setLoading(true);
        const response = await getPMEmployeeAppraisal(appraisalId);
        setAppraisal(response.data);

        // Set default language from appraisal
        if (response.data && response.data.DefaultLanguage) {
          setLanguage(response.data.DefaultLanguage);
        }

        // Load existing answers into form if not finished
        if (response.data && response.data.EmployeeStatus !== FINISHED) {
          const loadedFormData = {};

          response.data.Groups.forEach((group) => {
            group.Questions.forEach((question) => {
              if (question.IsEmployeeQuestion && question.Answers) {
                const employeeAnswers = question.Answers.filter(a => a.AnswerType === EMPLOYEE);

                employeeAnswers.forEach((answer) => {
                  const questionId = question.PMEmployeeAppraisalGroupQuestionID;

                  if (answer.IsComment || answer.Comment) {
                    // This is a comment
                    if (!loadedFormData[questionId]) {
                      loadedFormData[questionId] = {};
                    }
                    loadedFormData[questionId].comment = answer.Answer || answer.Comment;
                  } else {
                    // This is the main answer
                    if (!loadedFormData[questionId]) {
                      loadedFormData[questionId] = {};
                    }

                    let value = answer.Answer;

                    // Parse based on question type
                    if (question.QuestionType === 'Multiple') {
                      // Parse JSON array
                      try {
                        value = JSON.parse(value);
                      } catch (e) {
                        value = [];
                      }
                    } else if (question.QuestionType === 'Date') {
                      // Convert to Date object
                      value = value ? new Date(value) : null;
                    } else if (question.QuestionType === 'Single' || question.QuestionType === 'Scale') {
                      // Parse as number (OptionID)
                      value = parseInt(value, 10);
                    }
                    // For Text and Bool, keep as string

                    loadedFormData[questionId].value = value;
                  }
                });
              }
            });
          });

          setFormData(loadedFormData);
        }

        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (appraisalId) {
      fetchAppraisal();
    }
  }, [appraisalId]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Col xs={12} sm={10} smOffset={1} md={8} mdOffset={2}>
        <PanelMessage
          type="error"
          iconName="error"
          message={error.message || <FormattedMessage id="appraisal.error" />}
        />
      </Col>
    );
  }

  if (!appraisal) {
    return null;
  }

  const {
    EmployeeName,
    ManagerEmployeeName,
    EmployeeDueDate,
    FinalDueDate,
    EmployeeStatus,
    ManagerStatus,
    FinalStatus,
    Groups,
    Title,
    Description,
    DefaultLanguage,
    Languages,
    FinalResult,
  } = appraisal;

  const empDue = new Date(EmployeeDueDate);
  empDue.setHours(23, 59, 59);
  const dueDate = empDue;

  let finalDue = null;
  if (FinalDueDate) {
    finalDue = new Date(FinalDueDate);
    finalDue.setHours(23, 59, 59);
  }

  const isDueExpired = dueDate.getTime() < Date.now() || (finalDue && finalDue.getTime() < Date.now());
  const isFinished = EmployeeStatus === FINISHED || isDueExpired;
  const allFinished = EmployeeStatus === FINISHED && ManagerStatus === FINISHED && FinalStatus === FINISHED;

  const handleNav = () => {
    context.router.push('/pmappraisals');
  };

  const handleInputChange = (questionId, value, isComment = false) => {
    setFormData({
      ...formData,
      [questionId]: {
        ...formData[questionId],
        [isComment ? 'comment' : 'value']: value,
      },
    });
  };

  const transformFormValuesToAnswers = (values) => {
    const answers = [];

    Object.entries(values).forEach(([questionId, data]) => {
      if (!data) return;

      const { value, comment } = data;

      // Add main answer if value exists
      if (value !== undefined && value !== null && value !== '') {
        let answerValue = value;

        // Handle arrays (multiple choice)
        if (Array.isArray(value)) {
          answerValue = JSON.stringify(value);
        } else if (value instanceof Date) {
          answerValue = value.toISOString();
        } else if (typeof value !== 'string') {
          answerValue = String(value);
        }

        answers.push({
          PMEmployeeAppraisalQuestionID: parseInt(questionId, 10),
          Answer: answerValue,
          AnswerType: EMPLOYEE,
        });
      }

      // Add comment if exists
      if (comment) {
        answers.push({
          PMEmployeeAppraisalQuestionID: parseInt(questionId, 10),
          Answer: comment,
          AnswerType: EMPLOYEE,
          IsComment: true,
        });
      }
    });

    return answers;
  };

  const validateRequiredFields = () => {
    const errors = {};
    let isValid = true;

    if (Groups) {
      Groups.forEach((group) => {
        group.Questions.forEach((question) => {
          if (question.IsEmployeeQuestion && question.IsRequired) {
            const answerData = formData[question.PMEmployeeAppraisalGroupQuestionID];
            const value = answerData ? answerData.value : null;

            // Check if value is empty
            if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
              errors[question.PMEmployeeAppraisalGroupQuestionID] = true;
              isValid = false;
            }
          }
        });
      });
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (status) => {
    // Validate required fields only when finishing
    if (status === 'Finished') {
      if (!validateRequiredFields()) {
        return;
      }
    }

    const answers = transformFormValuesToAnswers(formData);
    const payload = {
      Answers: answers,
      EmployeeStatus: status,
    };

    try {
      setIsSaving(true);
      await updatePMEmployeeAppraisal(payload, appraisalId);

      // Success - navigate back or show success message
      if (status === 'Finished') {
        dispatch(addNotification({
          title: <FormattedMessage id="appraisal.notification.submitted" />,
          message: <FormattedMessage id="appraisal.notification.submitted.message" />,
          status: 'success',
          dismissible: true,
          dismissAfter: 4000,
        }));
        context.router.push('/pmappraisals');
      } else {
        dispatch(addNotification({
          title: <FormattedMessage id="appraisal.notification.saved" />,
          message: <FormattedMessage id="appraisal.notification.saved.message" />,
          status: 'success',
          dismissible: true,
          dismissAfter: 4000,
        }));
        // Clear validation errors on successful save
        setValidationErrors({});
      }
    } catch (err) {
      dispatch(addNotification({
        title: 'Error',
        message: err.message || 'Failed to save appraisal',
        status: 'error',
        dismissible: true,
        dismissAfter: 5000,
      }));
      console.error('Error submitting appraisal:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const getAnswerDisplayText = (question, answerType = EMPLOYEE) => {
    const answers = question.Answers ? question.Answers.filter(a => a.AnswerType === answerType && !a.IsComment) : [];
    if (!answers.length) return '-';

    const answer = answers[0];
    const { QuestionType, Options } = question;

    if (QuestionType === 'Text') {
      return answer.Answer || '-';
    }

    if (QuestionType === 'Bool') {
      const yesText = language === 'IS' ? 'Já' : 'Yes';
      const noText = language === 'IS' ? 'Nei' : 'No';
      return answer.Answer === 'true' ? yesText : noText;
    }

    if (QuestionType === 'Date') {
      return answer.Answer ? new Date(answer.Answer).toLocaleDateString(language === 'IS' ? 'is-IS' : 'en-US') : '-';
    }

    if (QuestionType === 'Single' || QuestionType === 'Scale') {
      const optionId = parseInt(answer.Answer, 10);
      const option = Options.find(o => o.OptionID === optionId);
      if (option) {
        const text = get(option, ['Text', language]) || get(option, ['Text', 'IS']) || get(option, ['Text', 'EN']) || '';
        return QuestionType === 'Scale' && option.ScaleValue ? `${text} (${option.ScaleValue})` : text;
      }
      return '-';
    }

    if (QuestionType === 'Multiple') {
      try {
        const optionIds = JSON.parse(answer.Answer);
        const selectedOptions = Options.filter(o => optionIds.includes(o.OptionID));
        return selectedOptions.map(o => get(o, ['Text', language]) || get(o, ['Text', 'IS']) || get(o, ['Text', 'EN']) || '').join(', ') || '-';
      } catch (e) {
        return '-';
      }
    }

    return '-';
  };

  const getAnswerComment = (question, answerType = EMPLOYEE) => {
    const answers = question.Answers ? question.Answers.filter(a => a.AnswerType === answerType && a.IsComment) : [];
    return answers.length ? answers[0].Answer : '';
  };

  // Use custom appraisal title from WebValues if exists
  const appraisalTitle = getAppraisalTitle(valuesByKey);

  return (
    <Col xs={12} md={10} mdOffset={1} style={{ paddingBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Subheader style={styles.subheader}>
          <FormattedMessage id={appraisalTitle} />
        </Subheader>
        {Languages && Languages.length > 1 && (
          <SelectField
            value={language}
            onChange={(e, index, value) => setLanguage(value)}
            style={{ width: 120 }}
          >
            {Languages.map(lang => (
              <MenuItem key={lang} value={lang} primaryText={lang} />
            ))}
          </SelectField>
        )}
      </div>

      <Paper style={{ marginBottom: 16 }}>
        <div style={styles.root}>
          <span style={styles.header}>{get(Title, language) || ''}</span>
          <span style={styles.appraisalDescription}>{get(Description, language) || ''}</span>
          <div style={styles.info}>
            <div style={styles.infoItem}>
              <span style={styles.infoMessage}>{EmployeeName}</span>
              <span style={styles.label}><FormattedMessage id="appraisal.employee" /></span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoMessage}>{ManagerEmployeeName}</span>
              <span style={styles.label}><FormattedMessage id="appraisal.manager" /></span>
            </div>
            <div style={styles.infoItem}>
              {dueDate.getTime() < Date.now() ?
                <span style={styles.errorText}><FormattedMessage id="appraisal.expired" /></span> :
                <span style={styles.infoMessage}>{fromNow(dueDate)}</span>
              }
              <span style={styles.label}><FormattedMessage id="appraisal.timeToFinish" /></span>
            </div>
          </div>
        </div>
      </Paper>
      {/*
      <Paper style={{ marginBottom: 16 }}>
        <div style={styles.root}>
          <span>{get(Description, DefaultLanguage) || ''}</span>
        </div>
      </Paper> */}

      <form>
        {Groups && Groups.map((group, groupIndex) => (
          <div key={group.PMAppraisalGroupID}>
            {group.Questions && group.Questions.some(q => q.IsEmployeeQuestion || (allFinished && q.IsManagerQuestion)) && (
              <div style={styles.groupTitle}>
                {get(group, ['Text', language]) || get(group, ['Text', 'IS']) || get(group, ['Text', 'EN']) || ''}
              </div>
            )}
            {group.Questions && group.Questions.map((question) => {
              // When not finished, only show employee questions
              // When all finished, show all questions
              if (!isFinished && !question.IsEmployeeQuestion) return null;
              if (isFinished && !allFinished && !question.IsEmployeeQuestion) return null;
              if (allFinished && !question.IsEmployeeQuestion && !question.IsManagerQuestion) return null;

              const {
                PMEmployeeAppraisalGroupQuestionID,
                Text,
                // eslint-disable-next-line
                Description,
                QuestionType,
                Options,
                AllowComment,
                IsEmployeeQuestion,
                IsManagerQuestion,
              } = question;

              const questionText = get(Text, language) || get(Text, 'IS') || get(Text, 'EN') || '';
              const descriptionText = get(Description, language) || get(Description, 'IS') || get(Description, 'EN') || '';
              const answerData = formData[PMEmployeeAppraisalGroupQuestionID] || {};
              const value = answerData.value || '';
              const comment = answerData.comment || '';
              const hasError = validationErrors[PMEmployeeAppraisalGroupQuestionID];
              const isRequired = question.IsRequired;

              return (
                <Paper key={PMEmployeeAppraisalGroupQuestionID} style={{ marginBottom: 16 }}>
                  <div style={styles.questionRoot}>
                    <div style={styles.question}>
                      <span>
                        {questionText}
                        {isRequired && <span style={{ color: 'rgb(244, 67, 54)', marginLeft: 4 }}>*</span>}
                      </span>
                      {descriptionText && <span style={styles.description}>{descriptionText}</span>}
                      {hasError && (
                        <span style={{ color: 'rgb(244, 67, 54)', fontSize: 12, marginTop: 4 }}>
                          <FormattedMessage id="editPanel.required" />
                        </span>
                      )}
                    </div>

                    <div style={styles.input}>
                      {isFinished ? (
                        // Read-only view
                        <div>
                          {/* Two-column layout for Employee and Manager answers */}
                          {allFinished && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 32,
                                marginBottom: 16,
                              }}
                            >
                              {/* Employee Answer Column - only if IsEmployeeQuestion */}
                              {IsEmployeeQuestion && (
                                <div style={{ flex: 1 }}>
                                  <span style={styles.label}><FormattedMessage id="qaelement.answer.employee" /></span>
                                  <div style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.87)' }}>
                                    {getAnswerDisplayText(question, EMPLOYEE)}
                                  </div>
                                  {AllowComment && getAnswerComment(question, EMPLOYEE) && (
                                    <div style={{ fontSize: 14, color: 'rgb(95, 95, 95)', marginTop: 8 }}>
                                      {getAnswerComment(question, EMPLOYEE)}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Manager Answer Column - only if IsManagerQuestion */}
                              {IsManagerQuestion && (
                                <div style={{ flex: 1 }}>
                                  <span style={styles.label}><FormattedMessage id="qaelement.answer.manager" /></span>
                                  <div style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.87)' }}>
                                    {getAnswerDisplayText(question, MANAGER)}
                                  </div>
                                  {AllowComment && getAnswerComment(question, MANAGER) && (
                                    <div style={{ fontSize: 14, color: 'rgb(95, 95, 95)', marginTop: 8 }}>
                                      {getAnswerComment(question, MANAGER)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Single Employee Answer - when not all finished */}
                          {!allFinished && (
                            <div style={{ marginBottom: 16 }}>
                              <span style={styles.label}><FormattedMessage id="qaelement.answer.employee" /></span>
                              <div style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.87)' }}>
                                {getAnswerDisplayText(question, EMPLOYEE)}
                              </div>
                              {AllowComment && getAnswerComment(question, EMPLOYEE) && (
                                <div style={{ fontSize: 14, color: 'rgb(95, 95, 95)', marginTop: 8 }}>
                                  {getAnswerComment(question, EMPLOYEE)}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Final Answer - full width, always show when allFinished */}
                          {allFinished && (
                            <div>
                              <span style={styles.label}><FormattedMessage id="qaelement.answer.final" /></span>
                              <div style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.87)' }}>
                                {getAnswerDisplayText(question, FINAL)}
                              </div>
                              {AllowComment && getAnswerComment(question, FINAL) && (
                                <div style={{ fontSize: 14, color: 'rgb(95, 95, 95)', marginTop: 8 }}>
                                  {getAnswerComment(question, FINAL)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Editable form
                        <React.Fragment>
                          {/* Text input */}
                          {QuestionType === 'Text' && (
                            <TextField
                              fullWidth
                              multiLine
                              rowsMax={6}
                              hintText={<FormattedMessage id="qaelement.input.text" />}
                              value={value}
                              onChange={(e, newValue) => handleInputChange(PMEmployeeAppraisalGroupQuestionID, newValue)}
                            />
                          )}

                          {/* Date input */}
                          {QuestionType === 'Date' && (
                            <DatePicker
                              fullWidth
                              hintText={<FormattedMessage id="time.Date" />}
                              value={value ? new Date(value) : null}
                              onChange={(e, date) => handleInputChange(PMEmployeeAppraisalGroupQuestionID, date)}
                              DateTimeFormat={global.Intl.DateTimeFormat}
                              locale={language === 'IS' ? 'is-IS' : 'en-US'}
                              cancelLabel={language === 'IS' ? 'Hætta við' : 'Cancel'}
                              okLabel={language === 'IS' ? 'Í lagi' : 'OK'}
                            />
                          )}

                          {/* Boolean - Yes/No */}
                          {QuestionType === 'Bool' && (
                            <RadioButtonGroup
                              name={`question-${PMEmployeeAppraisalGroupQuestionID}`}
                              valueSelected={value}
                              onChange={(e, newValue) => handleInputChange(PMEmployeeAppraisalGroupQuestionID, newValue)}
                            >
                              <RadioButton
                                value="true"
                                label={language === 'IS' ? 'Já' : 'Yes'}
                                style={{ marginBottom: 8 }}
                              />
                              <RadioButton
                                value="false"
                                label={language === 'IS' ? 'Nei' : 'No'}
                                style={{ marginBottom: 8 }}
                              />
                            </RadioButtonGroup>
                          )}

                          {/* Radio buttons - Single choice */}
                          {QuestionType === 'Single' && Options && (
                            <RadioButtonGroup
                              name={`question-${PMEmployeeAppraisalGroupQuestionID}`}
                              valueSelected={value}
                              onChange={(e, newValue) => handleInputChange(PMEmployeeAppraisalGroupQuestionID, newValue)}
                            >
                              {Options.map(option => (
                                <RadioButton
                                  key={option.OptionID}
                                  value={option.OptionID}
                                  label={get(option, ['Text', language])}
                                  style={{ marginBottom: 8 }}
                                />
                              ))}
                            </RadioButtonGroup>
                          )}

                          {/* Checkboxes - Multiple choice */}
                          {QuestionType === 'Multiple' && Options && (
                            <div>
                              {Options.map((option) => {
                                const selectedValues = Array.isArray(value) ? value : [];
                                const isChecked = selectedValues.includes(option.OptionID);
                                return (
                                  <Checkbox
                                    key={option.OptionID}
                                    label={get(option, ['Text', language])}
                                    checked={isChecked}
                                    onCheck={(e, checked) => {
                                      const newValues = checked
                                        ? [...selectedValues, option.OptionID]
                                        : selectedValues.filter(id => id !== option.OptionID);
                                      handleInputChange(PMEmployeeAppraisalGroupQuestionID, newValues);
                                    }}
                                    style={{ marginBottom: 8 }}
                                  />
                                );
                              })}
                            </div>
                          )}

                          {/* Scale - Rating */}
                          {QuestionType === 'Scale' && Options && (
                            <RadioButtonGroup
                              name={`question-${PMEmployeeAppraisalGroupQuestionID}`}
                              valueSelected={value}
                              onChange={(e, newValue) => handleInputChange(PMEmployeeAppraisalGroupQuestionID, newValue)}
                            >
                              {Options.map(option => (
                                <RadioButton
                                  key={option.OptionID}
                                  value={option.OptionID}
                                  label={`${get(option, ['Text', language])} ${option.ScaleValue ? `(${option.ScaleValue})` : ''}`}
                                  style={{ marginBottom: 8 }}
                                />
                              ))}
                            </RadioButtonGroup>
                          )}

                          {/* Comment field if allowed */}
                          {AllowComment && (
                            <TextField
                              fullWidth
                              multiLine
                              // rows={2}
                              rowsMax={4}
                              hintText="Add comment..."
                              floatingLabelText="Comment"
                              value={comment}
                              onChange={(e, newValue) => handleInputChange(PMEmployeeAppraisalGroupQuestionID, newValue, true)}
                              style={{ marginTop: 16 }}
                            />
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </Paper>
              );
            })}
          </div>
        ))}

        {!isFinished && (
          <div style={styles.actions}>
            <RaisedButton
              label={<FormattedMessage id="appraisal.save" />}
              onClick={() => handleSubmit('Started')}
              disabled={isSaving}
              style={styles.action}
            />
            <RaisedButton
              primary
              label={<FormattedMessage id="send" />}
              onClick={() => handleSubmit('Finished')}
              disabled={isSaving}
              style={styles.action}
            />
          </div>
        )}
      </form>

      {allFinished && FinalResult && (
        <Paper style={{ marginTop: 16, padding: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            <FormattedMessage id="appraisal.finalResult" />
          </div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
            {FinalResult}
          </div>
        </Paper>
      )}
    </Col>
  );
};

PMAppraisal.propTypes = {
  params: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  valuesByKey: PropTypes.object,
};

PMAppraisal.contextTypes = {
  router: PropTypes.object.isRequired,
};

export default connect(state => ({ valuesByKey: state.WebValuesReducer.valuesByKey }))(PMAppraisal);



// WEBPACK FOOTER //
// ./src/containers/pmappraisals/PMAppraisal.js